import {
  BiCaseBranch,
  BiDimensionCase,
  BiDimensionRef,
  BiFilterOp,
} from '@euler/model/bi/query';

/** The most bands a dimension takes, and the longest band name. */
export const kBiMaxBands = 20;
export const kBiMaxBandLabel = 40;

/** A band the form edits: names the rows below `below`. */
export interface BiBand {
  label: string;
  /** NaN while the field is blank or not a number. */
  below: number;
}

/**
 * The bands of a cased dimension when the form can edit them: every branch
 * an upper bound (`lt`), as the 分段 form writes them. Null for any other
 * case (a template's or the AI's, with `between` or `is_null`), which the
 * form keeps as it is and only offers to clear.
 */
export function biBandsOf(cased: BiDimensionCase): BiBand[] | null {
  const bands: BiBand[] = [];
  for (const branch of cased.branches) {
    if (
      branch.when.op !== BiFilterOp.Lt ||
      typeof branch.when.value !== 'number'
    )
      return null;
    bands.push({ label: branch.label, below: branch.when.value });
  }
  return bands;
}

/** The case the form's bands write, rows past the last bound named `rest`. */
export function biCaseOfBands(bands: BiBand[], rest: string): BiDimensionCase {
  const branches = bands.map(
    (band): BiCaseBranch => ({
      when: { op: BiFilterOp.Lt, value: band.below },
      label: band.label,
    }),
  );
  return { branches, else: rest };
}

/** A dimension banded for the first time: one empty band, then the rest. */
export function biBandedDimension(field: string): BiDimensionRef {
  return {
    field,
    key: `${field}_band`,
    case: { branches: [], else: '其他' },
  };
}

/** Why a cased dimension cannot be saved yet, or null. */
export function biCaseProblem(cased: BiDimensionCase): string | null {
  if (!cased.branches.length) return '分段至少需要一段';
  const labels = [...cased.branches.map(branch => branch.label), cased.else];
  if (labels.some(label => label !== undefined && !label.trim()))
    return '每一段都需要名称';
  if (
    labels.some(label => label !== undefined && label.length > kBiMaxBandLabel)
  )
    return `段名最多 ${kBiMaxBandLabel} 个字`;
  const bands = biBandsOf(cased);
  if (!bands) return null;
  if (bands.some(band => !Number.isFinite(band.below)))
    return '每一段都需要上限数值';
  if (bands.some((band, at) => at > 0 && band.below <= bands[at - 1].below))
    return '分段的上限要从小到大';
  return null;
}
